import React, { useState, useEffect } from 'react';
import './WarehouseManagement.css';

const WarehouseManagement = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(false);

  const [newLocation, setNewLocation] = useState({
    name: '',
    description: '',
    x: 0,
    y: 0,
    z: 0,
    width: 0,
    length: 0,
    height: 0,
    maxWeight: 0,
    locationType: 'Shelf'
  });

  const [newBarcode, setNewBarcode] = useState({
    barcode: '',
    barcodeType: 'Code128',
    productBarcode: ''
  });

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    setLoading(true);
    try {
      // API call to fetch warehouses
      const mockWarehouses = [
        { id: 1, name: 'Ana Depo', description: 'Merkez depo', capacity: 10000, usedCapacity: 6500 },
        { id: 2, name: 'Şube Depo', description: 'Şube deposu', capacity: 5000, usedCapacity: 3200 }
      ];
      setWarehouses(mockWarehouses);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
    setLoading(false);
  };

  const fetchLocations = async (warehouseId) => {
    setLoading(true);
    try {
      // API call to fetch warehouse locations
      const mockLocations = [
        { id: 1, name: 'A1', x: 1, y: 1, z: 1, width: 100, length: 100, height: 200, maxWeight: 500, isReserved: false },
        { id: 2, name: 'A2', x: 1, y: 2, z: 1, width: 100, length: 100, height: 200, maxWeight: 500, isReserved: true },
        { id: 3, name: 'B1', x: 2, y: 1, z: 1, width: 100, length: 100, height: 200, maxWeight: 500, isReserved: false }
      ];
      setLocations(mockLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
    setLoading(false);
  };

  const handleWarehouseSelect = (warehouse) => {
    setSelectedWarehouse(warehouse);
    fetchLocations(warehouse.id);
  };

  const handleAddLocation = async () => {
    setLoading(true);
    try {
      // API call to add new location
      const location = {
        id: Date.now(),
        ...newLocation,
        isReserved: false
      };
      setLocations([...locations, location]);
      setNewLocation({
        name: '',
        description: '',
        x: 0,
        y: 0,
        z: 0,
        width: 0,
        length: 0,
        height: 0,
        maxWeight: 0,
        locationType: 'Shelf'
      });
      setShowLocationModal(false);
    } catch (error) {
      console.error('Error adding location:', error);
    }
    setLoading(false);
  };

  const handleAddBarcode = async () => {
    setLoading(true);
    try {
      // API call to add new barcode
      const barcode = {
        id: Date.now(),
        ...newBarcode,
        warehouseLocationId: selectedLocation.id
      };
      setNewBarcode({
        barcode: '',
        barcodeType: 'Code128',
        productBarcode: ''
      });
      setShowBarcodeModal(false);
    } catch (error) {
      console.error('Error adding barcode:', error);
    }
    setLoading(false);
  };

  const generateWarehouseMap = () => {
    if (!selectedWarehouse || locations.length === 0) return null;

    const maxX = Math.max(...locations.map(l => l.x));
    const maxY = Math.max(...locations.map(l => l.y));
    const maxZ = Math.max(...locations.map(l => l.z));

    return (
      <div className="warehouse-map">
        <h3>Depo Haritası - {selectedWarehouse.name}</h3>
        <div className="map-container">
          {Array.from({ length: maxZ }, (_, z) => (
            <div key={z} className="floor">
              <h4>Kat {z + 1}</h4>
              <div className="floor-grid" style={{
                gridTemplateColumns: `repeat(${maxX}, 1fr)`,
                gridTemplateRows: `repeat(${maxY}, 1fr)`
              }}>
                {Array.from({ length: maxX * maxY }, (_, index) => {
                  const x = (index % maxX) + 1;
                  const y = Math.floor(index / maxX) + 1;
                  const location = locations.find(l => l.x === x && l.y === y && l.z === z + 1);
                  
                  return (
                    <div
                      key={`${x}-${y}-${z}`}
                      className={`map-cell ${location ? 'occupied' : 'empty'} ${location?.isReserved ? 'reserved' : ''}`}
                      onClick={() => location && setSelectedLocation(location)}
                    >
                      {location ? location.name : ''}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="warehouse-management">
      <div className="warehouse-header">
        <h1>Depo Yönetimi</h1>
        <div className="warehouse-stats">
          <div className="stat-card">
            <h3>Toplam Depo</h3>
            <p>{warehouses.length}</p>
          </div>
          <div className="stat-card">
            <h3>Toplam Konum</h3>
            <p>{locations.length}</p>
          </div>
          <div className="stat-card">
            <h3>Dolu Konum</h3>
            <p>{locations.filter(l => l.isReserved).length}</p>
          </div>
          <div className="stat-card">
            <h3>Boş Konum</h3>
            <p>{locations.filter(l => !l.isReserved).length}</p>
          </div>
        </div>
      </div>

      <div className="warehouse-content">
        <div className="warehouse-list">
          <h2>Depolar</h2>
          <div className="warehouse-grid">
            {warehouses.map(warehouse => (
              <div
                key={warehouse.id}
                className={`warehouse-card ${selectedWarehouse?.id === warehouse.id ? 'selected' : ''}`}
                onClick={() => handleWarehouseSelect(warehouse)}
              >
                <h3>{warehouse.name}</h3>
                <p>{warehouse.description}</p>
                <div className="capacity-bar">
                  <div 
                    className="capacity-fill" 
                    style={{ width: `${(warehouse.usedCapacity / warehouse.capacity) * 100}%` }}
                  ></div>
                </div>
                <p>Kapasite: {warehouse.usedCapacity}/{warehouse.capacity}</p>
              </div>
            ))}
          </div>
        </div>

        {selectedWarehouse && (
          <div className="warehouse-details">
            <div className="details-header">
              <h2>{selectedWarehouse.name} - Detaylar</h2>
              <button 
                className="btn btn-primary"
                onClick={() => setShowLocationModal(true)}
              >
                Yeni Konum Ekle
              </button>
            </div>

            <div className="location-list">
              <h3>Konumlar</h3>
              <div className="location-grid">
                {locations.map(location => (
                  <div
                    key={location.id}
                    className={`location-card ${location.isReserved ? 'reserved' : 'available'}`}
                    onClick={() => setSelectedLocation(location)}
                  >
                    <h4>{location.name}</h4>
                    <p>Koordinat: ({location.x}, {location.y}, {location.z})</p>
                    <p>Boyut: {location.width}x{location.length}x{location.height} cm</p>
                    <p>Maks. Ağırlık: {location.maxWeight} kg</p>
                    <span className={`status ${location.isReserved ? 'reserved' : 'available'}`}>
                      {location.isReserved ? 'Rezerve' : 'Müsait'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {generateWarehouseMap()}
          </div>
        )}
      </div>

      {/* Location Modal */}
      {showLocationModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Yeni Konum Ekle</h3>
            <form onSubmit={(e) => { e.preventDefault(); handleAddLocation(); }}>
              <div className="form-group">
                <label>Konum Adı:</label>
                <input
                  type="text"
                  value={newLocation.name}
                  onChange={(e) => setNewLocation({...newLocation, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Açıklama:</label>
                <textarea
                  value={newLocation.description}
                  onChange={(e) => setNewLocation({...newLocation, description: e.target.value})}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>X Koordinatı:</label>
                  <input
                    type="number"
                    value={newLocation.x}
                    onChange={(e) => setNewLocation({...newLocation, x: parseInt(e.target.value)})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Y Koordinatı:</label>
                  <input
                    type="number"
                    value={newLocation.y}
                    onChange={(e) => setNewLocation({...newLocation, y: parseInt(e.target.value)})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Z Koordinatı (Kat):</label>
                  <input
                    type="number"
                    value={newLocation.z}
                    onChange={(e) => setNewLocation({...newLocation, z: parseInt(e.target.value)})}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Genişlik (cm):</label>
                  <input
                    type="number"
                    value={newLocation.width}
                    onChange={(e) => setNewLocation({...newLocation, width: parseFloat(e.target.value)})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Uzunluk (cm):</label>
                  <input
                    type="number"
                    value={newLocation.length}
                    onChange={(e) => setNewLocation({...newLocation, length: parseFloat(e.target.value)})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Yükseklik (cm):</label>
                  <input
                    type="number"
                    value={newLocation.height}
                    onChange={(e) => setNewLocation({...newLocation, height: parseFloat(e.target.value)})}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Maksimum Ağırlık (kg):</label>
                <input
                  type="number"
                  value={newLocation.maxWeight}
                  onChange={(e) => setNewLocation({...newLocation, maxWeight: parseFloat(e.target.value)})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Konum Tipi:</label>
                <select
                  value={newLocation.locationType}
                  onChange={(e) => setNewLocation({...newLocation, locationType: e.target.value})}
                >
                  <option value="Shelf">Raf</option>
                  <option value="Floor">Zemin</option>
                  <option value="Pallet">Palet</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowLocationModal(false)}>
                  İptal
                </button>
                <button type="submit" disabled={loading}>
                  {loading ? 'Ekleniyor...' : 'Ekle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Barcode Modal */}
      {showBarcodeModal && selectedLocation && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Barkod Ekle - {selectedLocation.name}</h3>
            <form onSubmit={(e) => { e.preventDefault(); handleAddBarcode(); }}>
              <div className="form-group">
                <label>Barkod:</label>
                <input
                  type="text"
                  value={newBarcode.barcode}
                  onChange={(e) => setNewBarcode({...newBarcode, barcode: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Barkod Tipi:</label>
                <select
                  value={newBarcode.barcodeType}
                  onChange={(e) => setNewBarcode({...newBarcode, barcodeType: e.target.value})}
                >
                  <option value="Code128">Code 128</option>
                  <option value="Code39">Code 39</option>
                  <option value="QR">QR Code</option>
                  <option value="EAN13">EAN-13</option>
                </select>
              </div>
              <div className="form-group">
                <label>Ürün Barkodu (Opsiyonel):</label>
                <input
                  type="text"
                  value={newBarcode.productBarcode}
                  onChange={(e) => setNewBarcode({...newBarcode, productBarcode: e.target.value})}
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowBarcodeModal(false)}>
                  İptal
                </button>
                <button type="submit" disabled={loading}>
                  {loading ? 'Ekleniyor...' : 'Ekle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseManagement; 